import React from "react";
import { Button, message, Modal, Tag, Tooltip, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Connection } from "@/providers/smarta-documents/documents-api-client";
import { ApiPaths } from "@/providers/smarta-documents/documents-api-client";
import { Label, Tbody, Td, Tr, Value } from "../../components/styles";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";
import { AccountingSystem } from "./AccountingSystem";
import { api } from "../client";
import { usePollingCallback } from "@/lib/hooks/usePollingCallback";

const getTexts = (t: TFunction) => ({
  labels: {
    createdAt: t("connectionTable.labels.createdAt"),
    lastSyncedAt: t("connectionTable.labels.lastSyncedAt"),
    lastAddedAt: t("connectionTable.labels.lastAddedAt"),
    historicalStartDate: t("connectionTable.labels.historicalStartDate"),
    archivedAt: t("connectionTable.labels.archivedAt"),
    erroredAt: t("connectionTable.labels.erroredAt"),
    archived: t("connectionTable.labels.archived"),
    active: t("connectionTable.labels.active"),
    activeTooltip: t("connectionTable.labels.activeTooltip"),
    inactive: t("connectionTable.labels.inactive"),
    inactiveTooltip: t("connectionTable.labels.inactiveTooltip"),
    pending: t("connectionTable.labels.pending"),
    errored: t("connectionTable.labels.errored"),
  },
  documentTypes: {
    bills: t("connectionTable.documentTypes.bills"),
    invoices: t("connectionTable.documentTypes.invoices"),
    otherDocuments: t("connectionTable.documentTypes.otherDocuments"),
  },
  archiveModal: {
    title: t("connectionTable.archiveModal.title"),
    content: t("connectionTable.archiveModal.content"),
    okText: t("connectionTable.archiveModal.okText"),
    cancelText: t("connectionTable.archiveModal.cancelText"),
  },
  archiveButton: {
    archive: t("connectionTable.archiveButton.archive"),
    archiving: t("connectionTable.archiveButton.archiving"),
  },
  messages: {
    archivalSuccess: t("connectionTable.messages.archivalSuccess"),
    archivalError: t("connectionTable.messages.archivalError"),
  },
});

type Props = {
  data: Connection;
  customerId: string;
};

export const ConnectionTable: React.FC<Props> = ({ data, customerId }) => {
  const { t } = useTranslation("Documents");
  const texts = getTexts(t);
  const queryClient = useQueryClient();

  const { isPolling, startPolling } = usePollingCallback({
    interval: 2000,
    maxAttempts: 15, // 30 seconds max
  });

  const {
    targetSystem,
    historicalStartDate,
    createdAt,
    lastBillsSyncedAt,
    lastBillsAddedAt,
    lastInvoicesSyncedAt,
    lastInvoicesAddedAt,
    lastOtherDocumentsSyncedAt,
    lastOtherDocumentsAddedAt,
    archivedAt,
    state,
    erroredAt,
    errorCode,
  } = data;

  const { mutate: archiveConnection, isPending: isArchiving } = api.useMutation(
    "post",
    ApiPaths.postApiConnectionsByCustomerIdByTargetSystemArchive,
    {
      onSuccess: async () => {
        message.success(texts.messages.archivalSuccess);

        startPolling(async () => {
          // Refetch and wait for the result
          const result = await queryClient.fetchQuery({
            queryKey: [
              "get",
              ApiPaths.getApiConnectionsByCustomerId,
              { params: { path: { customerId } } },
            ],
          });

          const queryData = result as { data: Connection[] } | undefined;
          const connection = queryData?.data?.find(
            (c) => c.targetSystem === targetSystem
          );
          return connection?.state === "archived";
        });
      },
      onError: (error) => {
        message.error(error.error?.message || texts.messages.archivalError);
      },
    }
  );

  const handleArchiveClick = () => {
    Modal.confirm({
      title: texts.archiveModal.title,
      icon: <ExclamationCircleOutlined />,
      content: texts.archiveModal.content,
      okText: texts.archiveModal.okText,
      cancelText: texts.archiveModal.cancelText,
      onOk: () => {
        archiveConnection({
          params: {
            path: {
              customerId,
              targetSystem,
            },
          },
        });
      },
    });
  };

  const documentTypeRows = [
    {
      label: texts.documentTypes.bills,
      lastSyncedAt: lastBillsSyncedAt,
      lastAddedAt: lastBillsAddedAt,
    },
    {
      label: texts.documentTypes.invoices,
      lastSyncedAt: lastInvoicesSyncedAt,
      lastAddedAt: lastInvoicesAddedAt,
    },
    {
      label: texts.documentTypes.otherDocuments,
      lastSyncedAt: lastOtherDocumentsSyncedAt,
      lastAddedAt: lastOtherDocumentsAddedAt,
    },
  ];

  return (
    <Tbody>
      <Tr isHeaderRow hasBorder>
        <Td colSpan={2}>
          <AccountingSystem targetSystem={targetSystem} />
        </Td>
        <Td>
          <Label className="mt-2">{texts.labels.createdAt}</Label>
          <Value>{moment(createdAt).format(DISPLAY_DATE_TIME_FORMAT)}</Value>
        </Td>
        <Td>
          <Label>{texts.labels.historicalStartDate}</Label>
          <Value>
            {moment(historicalStartDate).format(DISPLAY_DATE_TIME_FORMAT)}
          </Value>
        </Td>
        <Td>
          {erroredAt && (
            <>
              <Label>{texts.labels.erroredAt}</Label>
              <Value>
                {moment(erroredAt).format(DISPLAY_DATE_TIME_FORMAT)}
              </Value>
            </>
          )}
          {archivedAt && (
            <>
              <Label className={erroredAt ? "mt-2" : undefined}>
                {texts.labels.archivedAt}
              </Label>
              <Value>
                {moment(archivedAt).format(DISPLAY_DATE_TIME_FORMAT)}
              </Value>
            </>
          )}
        </Td>
        <Td className="text-center">
          {state === "archived" ? (
            <Tag>
              <Typography.Text type="secondary">
                {texts.labels.archived}
              </Typography.Text>
            </Tag>
          ) : state === "pending" ? (
            <Tag color="processing">{texts.labels.pending}</Tag>
          ) : state === "errored" ? (
            <Tooltip title={errorCode}>
              <Tag color="error">{texts.labels.errored}</Tag>
            </Tooltip>
          ) : (
            <Tag color="success">{texts.labels.active}</Tag>
          )}
        </Td>
        <Td className="text-right">
          <Button
            type="link"
            size="small"
            onClick={handleArchiveClick}
            loading={isArchiving || isPolling}
            disabled={state === "archived" || isArchiving || isPolling}
          >
            {isArchiving || isPolling
              ? texts.archiveButton.archiving
              : texts.archiveButton.archive}
          </Button>
        </Td>
      </Tr>
      {state !== "pending" &&
        documentTypeRows.map((row) => (
          <Tr key={row.label}>
            <Td colSpan={2}>
              <Value>{row.label}</Value>
            </Td>
            <Td>
              <Label>{texts.labels.lastSyncedAt}</Label>
              <Value>
                {row.lastSyncedAt
                  ? moment(row.lastSyncedAt).format(DISPLAY_DATE_TIME_FORMAT)
                  : "-"}
              </Value>
            </Td>
            <Td>
              <Label>{texts.labels.lastAddedAt}</Label>
              <Value>
                {row.lastAddedAt
                  ? moment(row.lastAddedAt).format(DISPLAY_DATE_TIME_FORMAT)
                  : "-"}
              </Value>
            </Td>
            <Td />
            <Td className="text-center">
              {row.lastSyncedAt &&
              moment().diff(moment(row.lastSyncedAt), "hours") < 48 ? (
                <Tooltip title={texts.labels.activeTooltip}>
                  <Tag color="success">{texts.labels.active}</Tag>
                </Tooltip>
              ) : (
                <Tooltip title={texts.labels.inactiveTooltip}>
                  <Tag color="default">{texts.labels.inactive}</Tag>
                </Tooltip>
              )}
            </Td>
            <Td />
          </Tr>
        ))}
    </Tbody>
  );
};
